/* eslint-disable no-param-reassign */

import { cn } from "@/lib/cn";
import {
  getBlurhashFromUrl,
  getMobileMediaCapVars,
  useMediaLazyLoad,
} from "@/shared";
import Blurhash from "@components/blurhash";
import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  ImagesContainerStyled,
  StyledContainer,
} from "../styles/AnimatedImage.styled";
import type {
  AnimatedImageProps,
  EditorDataProps,
  EditorSlots,
} from "../types";

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};

export const AnimatedImageLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlots;
  editorData?: EditorDataProps;
}) => {
  const { view } = useViewContext();
  const { settings } = entity as StaticEntityType<AnimatedImageProps>;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { setObserverRef, isVisible } = useMediaLazyLoad();
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;

  const containerStyle = useMemo(() => {
    const isNarrowCombo = entity.layers?.combo?.settings?.maxWidth === "narrow";
    return {
      "--entity-size": `${width}%`,
      "--entity-height": `${height}px`,
      ...getMobileMediaCapVars(width, settings.shape, isNarrowCombo),
    } as CSSProperties;
  }, [width, height, settings.shape, entity.layers?.combo?.settings?.maxWidth]);

  let images = settings.images;
  const [idx, setIdx] = useState(0);
  const animInterval = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPdf = view === "pdf";

  if (isPdf) images = [images[images.length - 1]];

  useEffect(() => {
    setIdx(0);

    if (isPdf || !containerRef.current) {
      if (animInterval.current !== null) {
        clearInterval(animInterval.current);
      }
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();

          animInterval.current = setInterval(
            () => {
              setIdx((prevIndex) => {
                if (prevIndex === images.length - 1) {
                  if (settings.shouldLoop) return 0;
                  clearInterval(animInterval.current || undefined);
                  return prevIndex;
                }
                return prevIndex + 1;
              });
            },
            (settings.delay || 2) * 1000,
          );
        }
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (animInterval.current !== null) {
        clearInterval(animInterval.current);
      }
    };
  }, [settings.shouldLoop, settings.delay, images.length, isPdf]);

  useEffect(
    () => () => {
      if (animInterval.current !== null) {
        clearInterval(animInterval.current);
      }
    },
    [],
  );

  const hash = getBlurhashFromUrl(images[0]?.src);

  return (
    <StyledContainer
      $length={images.length}
      $horizontalAlignment={settings.horizontalAlignment}
      style={containerStyle}
      $shape={settings.shape}
      ref={(r) => {
        containerRef.current = r;
        const containerEditorRef = editorData?.containerRef;
        if (containerEditorRef) containerEditorRef.current = r;
        setObserverRef(r);
      }}
      className="tw-relative tw-flex tw-items-center"
    >
      {editorSlots?.resizeSlot}
      <div
        className="wrapper"
        {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
      >
        <ImagesContainerStyled
          $shape={settings.shape}
          $shadow={settings.isShadow}
          ref={editorData?.imageContainerRef}
          $round={settings.round}
        >
          {hash && !isMediaLoaded && <Blurhash blurhash={hash} />}
          {images.filter(Boolean).map((img, index) => (
            <div
              key={index}
              className={cn(
                "image-wrapper tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full",
                {
                  "tw-opacity-100": index === idx || isPdf,
                  "tw-opacity-0": index !== idx && !isPdf,
                },
              )}
            >
              {isVisible && (
                <img
                  src={img.src}
                  alt=""
                  role="presentation"
                  className="tw-block tw-w-full"
                  onLoad={() => index === 0 && setIsMediaLoaded(true)}
                />
              )}
            </div>
          ))}
        </ImagesContainerStyled>
      </div>
    </StyledContainer>
  );
};
